import React, { useState, useEffect } from 'react';
import {
  Database, Download, Upload, Clock, CheckCircle,
  AlertTriangle, Loader2, RefreshCw, HardDrive,
  FileArchive, Shield, Server, KeyRound, Wifi
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import api from '@/lib/api';

const defaultConfig = {
  auto_backup_enabled: false,
  backup_frequency: 'daily',
  backup_time: '03:00',
  retention_daily: 7,
  retention_weekly: 4,
  retention_monthly: 2,
  sftp_host: '',
  sftp_port: 22,
  sftp_username: '',
  sftp_password: '',
  sftp_private_key: '',
  sftp_remote_path: '/',
};

const BackupManagementPage = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(defaultConfig);
  const [passwordMasked, setPasswordMasked] = useState('');
  const [pkeySet, setPkeySet] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchBackups();
    fetchConfig();
    fetchStats();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await api.get('/super-admin/backups');
      setBackups(response.data.backups || []);
    } catch (err) {
      setError('Erro ao carregar backups');
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await api.get('/super-admin/backup-config');
      const data = response.data || {};
      setConfig({
        ...defaultConfig,
        ...data,
        sftp_password: '',
        sftp_private_key: '',
      });
      setPasswordMasked(data.sftp_password_masked || '');
      setPkeySet(!!data.sftp_private_key_set);
    } catch (err) {
      // Use defaults
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/super-admin/dashboard');
      setStats({
        total_tenants: response.data.total_tenants || 0,
        total_reports: response.data.total_reports || 0,
        total_users: response.data.total_users || 0,
      });
    } catch (err) {
      setStats({ total_tenants: 0, total_reports: 0, total_users: 0 });
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const resp = await api.post('/super-admin/backups');
      const status = resp.data?.backup?.status;
      if (status === 'completed') {
        setSuccess(resp.data?.message || 'Backup criado e enviado com sucesso.');
      } else {
        setError(resp.data?.message || 'Backup não completado. Verifica as configurações SFTP.');
      }
      fetchBackups();
      setTimeout(() => { setSuccess(''); setError(''); }, 6000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const handleTestSftp = async () => {
    setTesting(true);
    setError('');
    setSuccess('');
    try {
      const resp = await api.post('/super-admin/backups/test-sftp');
      if (resp.data?.ok) {
        setSuccess(resp.data.message || 'Ligação SFTP OK.');
      } else {
        setError(resp.data?.message || 'Falha na ligação SFTP.');
      }
      setTimeout(() => { setSuccess(''); setError(''); }, 6000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao testar SFTP');
    } finally {
      setTesting(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) return;
    setRestoring(true);
    setError('');
    try {
      await api.post(`/super-admin/backups/${selectedBackup.id}/restore`);
      setSuccess('Pedido de restauro registado.');
      setShowRestoreDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar restauro');
      setShowRestoreDialog(false);
    } finally {
      setRestoring(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    setError('');
    setSuccess('');
    try {
      const payload = { ...config };
      // Don't send empty strings for password/private key (backend ignores empty anyway)
      if (!payload.sftp_password) delete payload.sftp_password;
      if (!payload.sftp_private_key) delete payload.sftp_private_key;
      await api.put('/super-admin/backup-config', payload);
      setSuccess('Configurações de backup guardadas.');
      await fetchConfig();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatSize = (sizeMb) => {
    if (!sizeMb) return '0 MB';
    if (sizeMb >= 1024) return `${(sizeMb / 1024).toFixed(2)} GB`;
    return `${sizeMb.toFixed(1)} MB`;
  };

  const totalBackupSize = backups.reduce((sum, b) => sum + (b.size_mb || 0), 0);
  const sftpReady = !!(config.sftp_host && config.sftp_username && (config.sftp_password || passwordMasked || pkeySet || config.sftp_private_key));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  const statusBadge = (backup) => {
    const map = {
      completed: { label: 'Completo', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
      failed: { label: 'Falhou', cls: 'bg-red-100 text-red-700 border-red-200' },
      no_sftp_configured: { label: 'Sem SFTP', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    };
    const info = map[backup.status] || { label: backup.status || 'pendente', cls: 'bg-slate-100 text-slate-700 border-slate-200' };
    return <Badge variant="outline" className={info.cls}>{info.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="backup-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-indigo-700" />
            Gestão de Backups
          </h1>
          <p className="text-slate-500 mt-1">
            Backups automáticos SFTP com retenção 7 diários / 4 semanais / 2 mensais
          </p>
        </div>
        <Button
          onClick={handleCreateBackup}
          disabled={creating}
          className="bg-indigo-700 hover:bg-indigo-800 text-white"
          data-testid="create-backup-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Criar Backup Agora
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="backup-error-alert">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-emerald-50 border-emerald-200" data-testid="backup-success-alert">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><FileArchive className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Total Backups</p>
                <p className="text-xl font-bold text-slate-900">{backups.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg"><HardDrive className="w-5 h-5 text-indigo-700" /></div>
              <div>
                <p className="text-sm text-slate-500">Espaço Total</p>
                <p className="text-xl font-bold text-slate-900">{formatSize(totalBackupSize)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><Clock className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-sm text-slate-500">Último Backup</p>
                <p className="text-sm font-medium text-slate-900">
                  {backups[0] ? formatDate(backups[0].created_at) : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${sftpReady ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                <Server className={`w-5 h-5 ${sftpReady ? 'text-emerald-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500">Estado SFTP</p>
                <p className="text-sm font-medium text-slate-900">{sftpReady ? 'Configurado' : 'Por configurar'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Config */}
        <Card className="lg:col-span-1 bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-700" />
              Configuração SFTP + Agenda
            </CardTitle>
            <CardDescription className="text-slate-500">
              Envio automático diário. Retenção fixa: 7 diários / 4 semanais / 2 mensais.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-slate-900">Backup Automático Diário</Label>
              <Switch
                checked={config.auto_backup_enabled}
                onCheckedChange={(v) => setConfig(p => ({ ...p, auto_backup_enabled: v }))}
                data-testid="auto-backup-toggle"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Hora (UTC)</Label>
                <Input
                  type="time"
                  value={config.backup_time || '03:00'}
                  onChange={(e) => setConfig(p => ({ ...p, backup_time: e.target.value }))}
                  data-testid="backup-time-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Frequência</Label>
                <Select value={config.backup_frequency} onValueChange={(v) => setConfig(p => ({ ...p, backup_frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diário</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200 space-y-3">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Servidor SFTP</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1">
                  <Label className="text-slate-600 text-xs">Host</Label>
                  <Input
                    value={config.sftp_host || ''}
                    onChange={(e) => setConfig(p => ({ ...p, sftp_host: e.target.value }))}
                    placeholder="sftp.exemplo.com"
                    data-testid="sftp-host-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-600 text-xs">Porta</Label>
                  <Input
                    type="number"
                    value={config.sftp_port || 22}
                    onChange={(e) => setConfig(p => ({ ...p, sftp_port: parseInt(e.target.value || '22', 10) }))}
                    data-testid="sftp-port-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Utilizador</Label>
                <Input
                  value={config.sftp_username || ''}
                  onChange={(e) => setConfig(p => ({ ...p, sftp_username: e.target.value }))}
                  placeholder="cognietica-backups"
                  data-testid="sftp-user-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Password {passwordMasked && <span className="text-emerald-600">(guardada: {passwordMasked})</span>}</Label>
                <Input
                  type="password"
                  value={config.sftp_password}
                  onChange={(e) => setConfig(p => ({ ...p, sftp_password: e.target.value }))}
                  placeholder={passwordMasked ? '••••••• (não altera se em branco)' : ''}
                  data-testid="sftp-password-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Chave privada (opcional) {pkeySet && <span className="text-emerald-600">(guardada)</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={config.sftp_private_key}
                  onChange={(e) => setConfig(p => ({ ...p, sftp_private_key: e.target.value }))}
                  placeholder={pkeySet ? '••••• (não altera se em branco)' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                  data-testid="sftp-pkey-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-600 text-xs">Diretório Remoto</Label>
                <Input
                  value={config.sftp_remote_path || '/'}
                  onChange={(e) => setConfig(p => ({ ...p, sftp_remote_path: e.target.value }))}
                  placeholder="/backups/cognietica"
                  data-testid="sftp-remote-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleTestSftp}
                disabled={testing || !config.sftp_host || !config.sftp_username}
                className="w-full"
                data-testid="test-sftp-btn"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                Testar Ligação
              </Button>
            </div>
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleSaveConfig}
              disabled={savingConfig}
              className="w-full bg-indigo-700 hover:bg-indigo-800 text-white"
              data-testid="save-backup-config-btn"
            >
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Guardar Configuração
            </Button>
          </CardFooter>
        </Card>

        {/* List */}
        <Card className="lg:col-span-2 bg-white border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <FileArchive className="w-5 h-5 text-indigo-700" />
                  Histórico de Backups
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Últimos 50 backups (locais + remotos)
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchBackups}>
                <RefreshCw className="w-4 h-4 mr-2" />Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`backup-row-${backup.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className={`p-2 rounded-lg ${backup.type === 'auto' ? 'bg-blue-100' : 'bg-indigo-100'}`}>
                      <Database className={`w-5 h-5 ${backup.type === 'auto' ? 'text-blue-600' : 'text-indigo-700'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900">{formatDate(backup.created_at)}</p>
                        <Badge variant="outline" className={backup.type === 'auto' ? 'border-blue-200 text-blue-600' : 'border-indigo-200 text-indigo-700'}>
                          {backup.type === 'auto' ? 'Automático' : 'Manual'}
                        </Badge>
                        {statusBadge(backup)}
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {formatSize(backup.size_mb)}
                        {backup.filename && <> · <span className="font-mono">{backup.filename}</span></>}
                        {backup.error && <> · <span className="text-red-600">{backup.error}</span></>}
                      </p>
                      {backup.remote_path && (
                        <p className="text-xs text-slate-400 truncate">Remote: {backup.remote_path}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedBackup(backup); setShowRestoreDialog(true); }}
                      disabled={backup.status !== 'completed'}
                      data-testid={`restore-backup-${backup.id}`}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Restaurar
                    </Button>
                  </div>
                </div>
              ))}

              {backups.length === 0 && (
                <div className="text-center py-8">
                  <Database className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum backup ainda.</p>
                  <p className="text-sm text-slate-500">Configura o SFTP e clica em &quot;Criar Backup Agora&quot;.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Restore Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Confirmar Restauro
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              O restauro é executado manualmente pela equipa de operações — este pedido fica registado no log de auditoria.
            </DialogDescription>
          </DialogHeader>

          {selectedBackup && (
            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Data:</span><span>{formatDate(selectedBackup.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tamanho:</span><span>{formatSize(selectedBackup.size_mb)}</span></div>
              {selectedBackup.filename && (
                <div className="flex justify-between"><span className="text-slate-500">Ficheiro:</span><span className="font-mono text-xs">{selectedBackup.filename}</span></div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>Cancelar</Button>
            <Button onClick={handleRestore} disabled={restoring} className="bg-red-600 hover:bg-red-700 text-white">
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Solicitar Restauro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupManagementPage;
